/** @file Axes layer: world X/Y axes through the origin with numeric ticks pinned inside the viewport. */
import type { Camera } from '@pose2d/geometry/types';
import { affine } from '@pose2d/math/affine';
import type { Sink } from '../draw/createSink';
import { emitLine } from '../draw/emitLine';
import { emitRect } from '../draw/emitRect';
import { emitText } from '../draw/emitText';
import { fillSegment } from '../draw/fillSegment';
import type { OverlayInput } from '../overlayInput';
import { OVERLAY_STYLE } from '../overlayStyle';
import { pixelsPerUnit } from './pixelsPerUnit';
import { visibleWorldBox } from './visibleWorldBox';

const MAX_TICKS = 64;
const line = new Float64Array(4);
const p: [number, number] = [0, 0];
const worldBox: [number, number, number, number] = [0, 0, 0, 0];
/** Tick labels of both axes in draw order: text and top-left (scratch, refilled per call; no object per tick). */
const tickText: string[] = [];
const tickXY = new Float64Array(2 * 2 * MAX_TICKS);

/** 1, 2 or 5 times a power of ten, at least `raw`. */
function niceStep(raw: number): number {
  const pow = 10 ** Math.floor(Math.log10(raw));
  const n = raw / pow;
  return (n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10) * pow;
}

/** `value` snapped to the step, printed with the step's decimals (0.5 -> "0.5"); the caller skips the zero tick. */
function formatTick(value: number, step: number): string {
  const decimals = Math.max(0, -Math.floor(Math.log10(step) + 1e-9));
  return (Math.round(value / step) * step).toFixed(decimals);
}

/** Queues the label of the tick at pixel (tx, ty) on `axis`, 3-4 px off the tick and clamped 2 px inside the view. */
function queueLabel(sink: Sink, axis: 0 | 1, text: string, tx: number, ty: number): void {
  const { glyphW, glyphH } = sink.atlas, w = text.length * glyphW, k = tickText.length;
  const x = Math.min(Math.max(axis === 0 ? tx + 3 : tx + 4, 2), sink.width - w - 2);
  const y = Math.min(Math.max(axis === 0 ? ty + 4 : ty - glyphH / 2, 2), sink.height - glyphH - 2);
  tickText.push(text);
  tickXY[2 * k] = Math.round(x);
  tickXY[2 * k + 1] = Math.round(y);
}

/**
 * Tick marks along one axis (0 = X axis at y = 0, 1 = Y axis at x = 0), their labels queued for later. The zero tick
 * is skipped. Ticks are counted by index, not `v += step` (stuck past 2^53 steps out); a multiple rounding onto the
 * previous one is skipped.
 */
function axisTicks(sink: Sink, camera: Camera, axis: 0 | 1, step: number): void {
  visibleWorldBox(sink, worldBox);
  const lo = axis === 0 ? worldBox[0] : worldBox[1], hi = axis === 0 ? worldBox[2] : worldBox[3];
  const first = Math.ceil(lo / step), count = Math.min(MAX_TICKS, Math.floor(hi / step) - first + 1);
  for (let n = 0, prev = NaN; n < count; n++) {
    const v = (first + n) * step;
    if (v === prev || Math.abs(v) < step / 2) continue;
    prev = v;
    affine.apply(camera.worldToPixel, axis === 0 ? v : 0, axis === 0 ? 0 : v, p);
    const tx = p[0], ty = p[1];
    queueLabel(sink, axis, formatTick(v, step), tx, ty);
    const mark = axis === 0 ? fillSegment(line, tx, ty - 4, tx, ty + 4) : fillSegment(line, tx - 4, ty, tx + 4, ty);
    emitLine(sink, mark, 1, axis === 0 ? OVERLAY_STYLE.axisX : OVERLAY_STYLE.axisY);
  }
}

/** The queued tick labels: every box first, then every text (two batched passes). */
function tickLabels(sink: Sink): void {
  const { glyphW, glyphH } = sink.atlas, pad = OVERLAY_STYLE.labelPad;
  for (let k = 0; k < tickText.length; k++) {
    const x = tickXY[2 * k] ?? 0, y = tickXY[2 * k + 1] ?? 0;
    emitRect(sink, x - pad, y - pad, (tickText[k] ?? '').length * glyphW + 2 * pad, glyphH + 2 * pad, OVERLAY_STYLE.labelBox);
  }
  for (let k = 0; k < tickText.length; k++) emitText(sink, tickText[k] ?? '', tickXY[2 * k] ?? 0, tickXY[2 * k + 1] ?? 0, OVERLAY_STYLE.tickText);
}

/** The X axis (axis 0, world x from a to b at y = 0) or the Y axis (axis 1, world y from a to b at x = 0). */
function axisLine(sink: Sink, camera: Camera, axis: 0 | 1, a: number, b: number): void {
  affine.apply(camera.worldToPixel, axis === 0 ? a : 0, axis === 0 ? 0 : a, p);
  const x0 = p[0], y0 = p[1];
  affine.apply(camera.worldToPixel, axis === 0 ? b : 0, axis === 0 ? 0 : b, p);
  emitLine(sink, fillSegment(line, x0, y0, p[0], p[1]), 1.5, axis === 0 ? OVERLAY_STYLE.axisX : OVERLAY_STYLE.axisY);
}

/** `options.axes`: X axis red, Y axis green, ticks every 1/2/5 x 10^k units at least 64 px apart. */
export function drawAxes(sink: Sink, input: OverlayInput): void {
  if (input.options.axes !== true) return;
  const { camera } = input;
  visibleWorldBox(sink, worldBox);
  const x0 = worldBox[0], y0 = worldBox[1], x1 = worldBox[2], y1 = worldBox[3];
  axisLine(sink, camera, 0, x0, x1);
  axisLine(sink, camera, 1, y0, y1);
  const step = niceStep(OVERLAY_STYLE.minTickPx / pixelsPerUnit(camera));
  if (!Number.isFinite(step) || step <= 0) return;
  tickText.length = 0;
  axisTicks(sink, camera, 0, step);
  axisTicks(sink, camera, 1, step);
  tickLabels(sink);
  tickText.length = 0;
}
